import { Table } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import isEmpty from 'lodash/isEmpty'

import { observer } from 'mobx-react'

import { getFilterValue } from 'modules/core/components/EmployeeMultiFilters/commonFilters'
import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { orderBy } from 'modules/core/lib/filters'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import { matchText } from 'modules/core/lib/stringUtils'
import { employeeUser } from 'modules/core/store/collections/users'

import presences from 'modules/attendance/store/collections/presences'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import planningVersions, {
  PlanningVersionProvider,
} from 'modules/time_planning/store/collections/planningVersions'
import { workScheduleEmployees } from 'modules/work_schedules/lib/workScheduleEmployees'
import type WorkSchedule from 'modules/work_schedules/store/models/Schedule'

import Body from './Body'
import TableHeader from './Header'
import SearchBar from './Header/SearchBar'

const useSortedEmployees = (employees: Employee[], form: FiltersForm) => {
  const { loading, value: sortedEmployees } = useAsyncData(
    () => orderBy(form, employees),
    [
      form.get('filterBy').value,
      form.get('sortDirection').value,
      form.get('sortAttribute').value,
      employees.length,
    ]
  )

  return {
    loading,
    sortedEmployees: sortedEmployees ?? [],
  }
}

const useSearchFilter = createComputed((search: string, employees: Employee[]) => {
  return employees.filter(
    (employee) => isEmpty(search) || matchText(search, employeeUser(employee).fullName())
  )
})

interface Props {
  workSchedule: WorkSchedule
  filtersForm: FiltersForm
  contractVersions: ContractVersions
}

const EmployeesTable = ({ workSchedule, filtersForm, contractVersions }: Props) => {
  const scheduleEmployees = workScheduleEmployees(workSchedule, employees)

  // It's necessary use this hook to update the filteredEmployees used in the filter component
  useEffect(() => {
    Promise.all([
      planningVersions.fetch({
        data: { employee_ids: workSchedule.employeeIds, planning_tool: 'work_schedule' },
      }),
      presences.fetch({ withCachedFetchParams: true }),
    ])
    onApplyFilters(scheduleEmployees, contractVersions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleEmployees.length])

  const {
    filteredModels: filteredEmployees,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, LOCAL_STORAGE_KEYS.employeesFilters)

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { sortedEmployees } = useSortedEmployees(filteredEmployees, filtersForm)

  const employeesToShow = useSearchFilter(debouncedSearch, sortedEmployees).get()

  const TableOptions = () => (
    <SearchBar
      filtersForm={filtersForm}
      onSearch={onSearch}
      search={search}
      onAddFilter={onAddFilter}
      onRemoveFilter={onRemoveFilter}
      onApplyFilters={(defaultFilter) =>
        onApplyFilters(scheduleEmployees, contractVersions, defaultFilter)
      }
      onCancel={onCancel}
      filters={filters}
      logicOperator={logicOperator}
    />
  )

  return (
    <PlanningVersionProvider collection={planningVersions}>
      <Table fullWidth options={TableOptions}>
        <TableHeader filtersForm={filtersForm} />
        <Body employees={employeesToShow} />
      </Table>
    </PlanningVersionProvider>
  )
}

export default observer(EmployeesTable)
